import {
  comparePages as researchedCompetitors,
  type CompetitorPage,
} from "@/data/compare";
import { expansionCompetitors } from "@/data/compare-expansion";
import {
  FREE_APPLICATION_CREDITS_DESCRIPTION,
  FREE_TRIAL_APPLICATION_CREDITS,
} from "@src/config/freeTrial";

export const COMPETITOR_REVIEWED_AT = "July 24, 2026";
export const COMPETITOR_VERIFIED_ON = "2026-07-24";
const CLAIM_FRESHNESS_DAYS = 30;

export type ProductWorkflow =
  | "browser_extension"
  | "cloud_agent"
  | "managed_service"
  | "web_workspace";

export type CapabilityLevel = "yes" | "limited" | "review_required" | "no" | "not_confirmed";

export type ProductCapabilities = {
  jobDiscovery: CapabilityLevel;
  autofill: CapabilityLevel;
  autoSubmit: CapabilityLevel;
  resumeTailoring: CapabilityLevel;
  coverLetters: CapabilityLevel;
  tracking: CapabilityLevel;
};

export type ProductSummary = {
  slug: string;
  name: string;
  category: string;
  officialUrl: string;
  productModel: string;
  workflow: ProductWorkflow;
  capabilities: ProductCapabilities;
  pricingSummary: string;
  freeAccess: string;
  userControl: string;
  bestFit: string;
  limitation: string;
  verifiedAt: string;
  verifiedOn: string;
};

export type CompetitorProfile = CompetitorPage & ProductSummary;

export type AlternativePage = {
  slug: string;
  competitorName: string;
  title: string;
  metaDescription: string;
  directAnswer: string;
  reasonsToSwitch: string[];
  recommendationSlugs: string[];
  verdictHeading?: string;
  verdictBody?: string;
  ctaLabel?: string;
  ctaHeading?: string;
  ctaDescription?: string;
};

type ProfileMetadata = Pick<
  ProductSummary,
  | "productModel"
  | "workflow"
  | "capabilities"
  | "pricingSummary"
  | "freeAccess"
  | "userControl"
  | "bestFit"
  | "limitation"
> & {
  verifiedAt?: string;
  verifiedOn?: string;
};

const profileMetadata: Record<string, ProfileMetadata> = {
  simplify: {
    productModel: "Copilot autofill and job-search workspace; Autopilot in private early access",
    workflow: "browser_extension",
    capabilities: {
      jobDiscovery: "yes",
      autofill: "yes",
      autoSubmit: "limited",
      resumeTailoring: "yes",
      coverLetters: "yes",
      tracking: "yes",
    },
    pricingSummary:
      "Free core tools; Simplify+ renews at $19.99 per week, $39.99 per month, or $89.99 per three months. Autopilot beta is limited to a subset of paid members, with 20 successful submissions per week and no rollover or top-ups.",
    freeAccess: "Copilot autofill, job matching, resume tools, and application tracking are available for free.",
    userControl: "Copilot users review, edit, and submit applications themselves. In private-early-access Autopilot beta, users select jobs and send immediately or from a queue; there is no final field-by-field review before submission.",
    bestFit: "Candidates seeking free Copilot autofill and tracking, or evaluating private-early-access Autopilot if they can access it.",
    limitation: "Copilot requires user submission. Autopilot is in private early access for a subset of Simplify+ members; confirm access, terms, and supported applications. Automatic daily-match queueing and scheduled sending are described as future work.",
    verifiedAt: "September 17, 2026",
    verifiedOn: "2026-09-17",
  },
  teal: {
    productModel: "Resume builder and job-search organization workspace",
    workflow: "web_workspace",
    capabilities: {
      jobDiscovery: "limited",
      autofill: "limited",
      autoSubmit: "no",
      resumeTailoring: "yes",
      coverLetters: "yes",
      tracking: "yes",
    },
    pricingSummary:
      "Free plan; published Teal+ prices are $13 every 7 days, $29 every 30 days, or $79 every 90 days. Paid subscriptions renew automatically unless canceled.",
    freeAccess:
      "The free plan includes unlimited resumes and job tracking plus limited AI, analysis, keyword-matching, and cover-letter credits.",
    userControl:
      "The candidate selects roles and can review or edit autofilled answers before completing the application.",
    bestFit: "Candidates who want a polished command center for a deliberate search.",
    limitation:
      "Its application workflow is candidate-directed and reviewable rather than a hands-off recurring application agent.",
    verifiedAt: "September 17, 2026",
    verifiedOn: "2026-09-17",
  },
  huntr: {
    productModel: "Resume, job-tracking, and application-autofill workspace",
    workflow: "browser_extension",
    capabilities: {
      jobDiscovery: "limited",
      autofill: "yes",
      autoSubmit: "no",
      resumeTailoring: "yes",
      coverLetters: "yes",
      tracking: "yes",
    },
    pricingSummary:
      "Free plan; Pro is $40 monthly, $90 quarterly, or $160 every six months.",
    freeAccess:
      "The free plan includes unlimited base resumes and application autofills, two tailored resumes, two application packets, and tracking for up to 100 jobs.",
    userControl: "The candidate opens, reviews, edits, and submits each application.",
    bestFit: "Candidates who want resume help and a visual application tracker.",
    limitation: "Autofill still requires the candidate to source and drive the application workflow.",
    verifiedAt: "September 17, 2026",
    verifiedOn: "2026-09-17",
  },
  lazyapply: {
    productModel: "High-volume browser-based application automation",
    workflow: "browser_extension",
    capabilities: {
      jobDiscovery: "limited",
      autofill: "yes",
      autoSubmit: "yes",
      resumeTailoring: "limited",
      coverLetters: "limited",
      tracking: "yes",
    },
    pricingSummary:
      "Annual plans: Basic $99 with 15 applications/day, Premium $149 with 150/day, Ultimate $999 with 1,500/day. These are advertised allowances, not guaranteed completed applications.",
    freeAccess: "No free trial is advertised; a live demo can be requested by email.",
    userControl: "The candidate configures filters and supervises browser automation.",
    bestFit: "Candidates prioritizing application volume on supported job boards.",
    limitation: "A volume-first workflow requires careful targeting and monitoring.",
    verifiedAt: "September 17, 2026",
    verifiedOn: "2026-09-17",
  },
  loopcv: {
    productModel: "Cloud job-matching, auto-apply, outreach, and tracking platform",
    workflow: "cloud_agent",
    capabilities: {
      jobDiscovery: "yes",
      autofill: "yes",
      autoSubmit: "yes",
      resumeTailoring: "limited",
      coverLetters: "limited",
      tracking: "yes",
    },
    pricingSummary:
      "Observed monthly cards: Standard $19.99 for up to 100 applications or emails; Premium $59.99 for up to 300; Done For You $89.99. The FAQ separately says paid plans start at €9.99, so confirm currency and checkout total.",
    freeAccess: "Free without a card: the Basic card lists one search, three boards and up to 10 applications or emails per month.",
    userControl: "Auto-Apply can be disabled per search or globally while matching continues. One-Click Apply lets users select matched jobs on demand; automated outreach has a separate setting.",
    bestFit: "Candidates who want recurring job discovery, applications, and recruiter outreach.",
    limitation: "Plan quotas combine applications and emails, not guaranteed submissions. Auto-Apply uses the stored CV; tailored screening answers and a separate letter generator do not establish automatic per-job resume rewriting.",
    verifiedAt: "September 17, 2026",
    verifiedOn: "2026-09-17",
  },
  sonara: {
    productModel: "AI job discovery and recurring application service",
    workflow: "cloud_agent",
    capabilities: {
      jobDiscovery: "yes",
      autofill: "yes",
      autoSubmit: "limited",
      resumeTailoring: "not_confirmed",
      coverLetters: "not_confirmed",
      tracking: "not_confirmed",
    },
    pricingSummary:
      "Sonara's active signup flow references recurring service terms; verify the current offer before subscribing.",
    freeAccess: "No public free tier or trial was verified; confirm the current signup offer.",
    userControl: "Terms describe editable job queues and applying on your behalf, but the public setup page says the user hits submit. Confirm the actual submission and review controls in your account.",
    bestFit: "Candidates looking for recurring AI-assisted job discovery.",
    limitation: "Public sources differ on final submission. Exact prices, allowances, resume tailoring, cover letters and submitted-application tracking were not established by this review.",
    verifiedAt: "September 17, 2026",
    verifiedOn: "2026-09-17",
  },
  aiapply: {
    productModel: "All-in-one AI resume, interview, and auto-apply toolkit",
    workflow: "cloud_agent",
    capabilities: {
      jobDiscovery: "yes",
      autofill: "yes",
      autoSubmit: "yes",
      resumeTailoring: "yes",
      coverLetters: "yes",
      tracking: "yes",
    },
    pricingSummary:
      "Recurring Auto Apply plans include $49/month for 100, $99/month for 250, or $199/quarter for 750 credits. Resume and required-cover-letter tailoring each add $12/month. Check higher-volume offers: the help articles disagree on the 1,000-credit price.",
    freeAccess: "Account signup is available; Auto Apply uses paid application credits.",
    userControl: "Auto sends matching jobs automatically; Hybrid holds weaker matches; Review requires job approval. Users set daily volume and can inspect submitted documents. Job approval is not evidence of field-by-field review.",
    bestFit: "Candidates who want resumes, cover letters, interviews, and applying in one toolkit.",
    limitation: "The 750-credit quarterly allowance is available upfront, not 250/month. Successful submissions consume credits; tailoring is an add-on, and a cover letter is sent only when required.",
    verifiedAt: "September 17, 2026",
    verifiedOn: "2026-09-17",
  },
  applypass: {
    productModel: "Automated job applications with optional coaching services",
    workflow: "cloud_agent",
    capabilities: {
      jobDiscovery: "yes",
      autofill: "yes",
      autoSubmit: "yes",
      resumeTailoring: "limited",
      coverLetters: "yes",
      tracking: "yes",
    },
    pricingSummary:
      "Basic is free for 7 applications per week; Momentum is $99 monthly for 100 per week, and Premium is $199 monthly for 400 per week.",
    freeAccess: "The free plan includes one application per day; a 100-application Premium trial is also advertised.",
    userControl: "Users set profile/preferences and provide match feedback; paid features include company exclusions. Auto-Apply sends applications on their behalf and records activity in a dashboard.",
    bestFit: "Candidates seeking weekly automated application allowances with optional interview coaching.",
    limitation: "The trial excludes company blocking and other starred pricing features. Resume overhauls are paid coaching add-ons; coaching does not mean humans submit every application.",
    verifiedAt: "September 17, 2026",
    verifiedOn: "2026-09-17",
  },
  careerflow: {
    productModel: "AI resume, LinkedIn, tracking, and job-search workspace",
    workflow: "browser_extension",
    capabilities: {
      jobDiscovery: "limited",
      autofill: "yes",
      autoSubmit: "no",
      resumeTailoring: "yes",
      coverLetters: "yes",
      tracking: "yes",
    },
    pricingSummary:
      "Basic is free. Premium: $8.99/week or $23.99/month; Premium Plus: $19.99/week or $44.99/month. Annual totals are $172.99 and $299.99, not the lower monthly-equivalent headlines.",
    freeAccess: "Basic is free with one resume and limited tracker/autofill features; help documentation lists a ten-job tracking limit.",
    userControl: "The candidate owns the search and uses AI tools to prepare and organize it.",
    bestFit: "Candidates who want LinkedIn, resume, and tracking help in one workspace.",
    limitation: "It assists a self-directed search rather than taking responsibility for ongoing submissions.",
    verifiedAt: "September 17, 2026",
    verifiedOn: "2026-09-17",
  },
  jobright: {
    productModel: "AI job-search suite with matching, autofill, an automated-apply Agent, resume tools, and referrals",
    workflow: "cloud_agent",
    capabilities: {
      jobDiscovery: "yes",
      autofill: "yes",
      autoSubmit: "yes",
      resumeTailoring: "yes",
      coverLetters: "yes",
      tracking: "yes",
    },
    pricingSummary:
      "Jobright advertises free candidate access with daily limits on features such as resume tailoring and a paid option for more use. Exact candidate prices and Agent allowances were not established on the inspected public pages.",
    freeAccess: "Free candidate access is advertised, with daily limits on some features; do not assume unlimited free Agent applications.",
    userControl: "Users can choose reviewable one-click autofill or Jobright's automated-apply Agent.",
    bestFit: "Candidates who want discovery, fit scoring, referrals, and multiple application workflows in one suite.",
    limitation: "The homepage describes primarily U.S. coverage. Exact Agent controls, prices and allowances need confirmation in the candidate product.",
    verifiedAt: "September 17, 2026",
    verifiedOn: "2026-09-17",
  },
  jobcopilot: {
    productModel: "Cloud application agent with matching and career tools",
    workflow: "cloud_agent",
    capabilities: {
      jobDiscovery: "yes",
      autofill: "yes",
      autoSubmit: "yes",
      resumeTailoring: "yes",
      coverLetters: "yes",
      tracking: "yes",
    },
    pricingSummary:
      "Premium starts from $0.93/day for up to 20 daily matches; Elite from $1.05/day for up to 50. These are advertised daily equivalents, not daily billing: confirm the weekly, monthly or quarterly total.",
    freeAccess: "No standing free plan was verified; the pricing page provides a try-it-now path.",
    userControl: "Users configure preferences and can save applications for review or enable automatic applying. Editing answers trains the copilot for future forms.",
    bestFit: "Candidates who want a cloud agent applying after an initial setup.",
    limitation: "Daily matches are not guaranteed successful submissions. Automatic per-application resume tailoring is listed on Elite; do not assume it is included in every plan.",
    verifiedAt: "September 17, 2026",
    verifiedOn: "2026-09-17",
  },
  jobscan: {
    productModel: "ATS resume optimization platform with review-first auto apply",
    workflow: "web_workspace",
    capabilities: {
      jobDiscovery: "yes",
      autofill: "yes",
      autoSubmit: "review_required",
      resumeTailoring: "yes",
      coverLetters: "yes",
      tracking: "yes",
    },
    pricingSummary:
      "Premium is $49.95/month or $89.95 every three months. The Auto Apply page lists extra packs at $8.50/5, $15/10 or $35/25 credits and a monthly top-up to two for Premium; confirm account allowances and discounts.",
    freeAccess: "The free plan lists five resume scans/month, a builder and tracker. A seven-day Premium trial is offered on the quarterly plan, then renews unless canceled; do not infer free Auto Apply from free scanning.",
    userControl: "Jobscan says every application field can be reviewed and edited, and nothing is submitted without the candidate's approval.",
    bestFit: "Candidates whose main concern is ATS alignment and resume quality.",
    limitation: "Auto Apply is review-first, not unattended sending. Its resume match score is a preparation aid, not an employer's actual ATS score or a hiring guarantee.",
    verifiedAt: "September 17, 2026",
    verifiedOn: "2026-09-17",
  },
  "jobhire-ai": {
    productModel: "AI job-search suite with optional automatic applications",
    workflow: "cloud_agent",
    capabilities: {
      jobDiscovery: "yes",
      autofill: "yes",
      autoSubmit: "yes",
      resumeTailoring: "yes",
      coverLetters: "yes",
      tracking: "yes",
    },
    pricingSummary:
      "Standard, Pro, and Extended subscriptions are available; exact prices appear after registration, and the official FAQ says there is no free trial.",
    freeAccess: "No free trial is advertised.",
    userControl: "The FAQ describes instant auto-apply, a 24-hour delay, or manual approval before a job enters the submission queue.",
    bestFit: "Candidates who want matching, materials, tracking, and applying in one broad suite.",
    limitation: "Exact prices appear after registration; the homepage advertises U.S. roles only.",
    verifiedAt: "September 17, 2026",
    verifiedOn: "2026-09-17",
  },
  applyghost: {
    productModel: "Server-side application agent for technical roles",
    workflow: "cloud_agent",
    capabilities: {
      jobDiscovery: "yes",
      autofill: "yes",
      autoSubmit: "yes",
      resumeTailoring: "limited",
      coverLetters: "limited",
      tracking: "yes",
    },
    pricingSummary:
      "Published monthly plans are €29 for 10 applications a week, €59 for 30, and €99 for 75.",
    freeAccess: "10 free applications without a credit card.",
    userControl: "Role, seniority, location, salary, work arrangement, blocked-company, and fit-threshold controls.",
    bestFit: "Technical candidates who want a server-side agent and automatic resume selection.",
    limitation: "The product is intentionally limited to technical and tech-adjacent roles.",
    verifiedAt: "August 20, 2026",
    verifiedOn: "2026-08-20",
  },
  "scale-jobs": {
    productModel: "Human-managed job application service",
    workflow: "managed_service",
    capabilities: {
      jobDiscovery: "yes",
      autofill: "yes",
      autoSubmit: "yes",
      resumeTailoring: "limited",
      coverLetters: "yes",
      tracking: "yes",
    },
    pricingSummary:
      "One-time base packages are $199 for 250 applications, $299 for 500, and $399 for 1,000. Job finding adds $99/$149/$199 respectively; AI-custom resumes and other services can also cost extra.",
    freeAccess: "The FAQ offers a free cover-letter tool preview after login; human-managed application packages are paid.",
    userControl: "Candidates communicate with an assistant and receive dashboard and proof-of-work updates.",
    bestFit: "Candidates who prefer people, not software, to complete applications.",
    limitation: "Human-assistant packages require a larger upfront payment and service turnaround.",
    verifiedAt: "September 17, 2026",
    verifiedOn: "2026-09-17",
  },
  massive: {
    productModel: "AI job matching and autopilot application service",
    workflow: "cloud_agent",
    capabilities: {
      jobDiscovery: "yes",
      autofill: "yes",
      autoSubmit: "yes",
      resumeTailoring: "yes",
      coverLetters: "yes",
      tracking: "yes",
    },
    pricingSummary:
      "The official homepage advertises up to 200 applications a month but does not display a public plan price.",
    freeAccess: "No public free-plan terms are displayed on the homepage.",
    userControl: "Candidates can select jobs or enable Autopilot. The FAQ advertises document previews before sending and visibility into submitted applications, not a mandatory approval gate for every application.",
    bestFit: "Candidates who value Massive's matched job inventory, custom materials, and visa filter.",
    limitation: "Pricing is not transparent on the public homepage.",
    verifiedAt: "September 17, 2026",
    verifiedOn: "2026-09-17",
  },
  applygenie: {
    productModel: "Review-before-submit browser autofill assistant",
    workflow: "browser_extension",
    capabilities: {
      jobDiscovery: "not_confirmed",
      autofill: "yes",
      autoSubmit: "no",
      resumeTailoring: "not_confirmed",
      coverLetters: "not_confirmed",
      tracking: "not_confirmed",
    },
    pricingSummary:
      "One-time packs are $9 for 25 supported applications, $29 for 100, and $69 for 400.",
    freeAccess: "Three-day trial without a credit card.",
    userControl: "The candidate opens, reviews, and submits each LinkedIn Easy Apply or Lever form.",
    bestFit: "Candidates who want inexpensive autofill with final approval on supported sites.",
    limitation: "Current support is limited to LinkedIn Easy Apply and Lever, and the user still submits every form.",
    verifiedAt: "September 17, 2026",
    verifiedOn: "2026-09-17",
  },
};

const supplementalCompetitors: CompetitorPage[] = [
  {
    slug: "jobright",
    name: "Jobright",
    category: "AI job matching, autofill, resume tailoring, and referral discovery",
    title: "LifeShack vs Jobright",
    metaTitle: "LifeShack vs Jobright | AI Job Search Comparison",
    metaDescription:
      "Compare LifeShack and Jobright for AI job matching, autofill, tailored resumes, referrals, application tracking, and hands-off job search.",
    officialUrl: "https://jobright.ai/",
    pricingNote:
      "Jobright advertises free candidate access with daily limits on some features, including resume tailoring, and a paid option for more use. Exact candidate prices and Agent application allowances were not established on the inspected public pages.",
    bestFor: [
      "Job seekers who want a large personalized job feed",
      "Candidates who value match explanations and referral discovery",
      "Applicants who want both reviewable autofill and an automated-apply option",
    ],
    watchouts: [
      "The product spans discovery, materials, networking, and applications rather than focusing only on submission",
      "The one-click autofill workflow still asks the candidate to submit the application",
      "The homepage describes primarily U.S. coverage; exact Agent controls and candidate-plan allowances need confirmation",
    ],
    verdict:
      "Choose Jobright when you want discovery, fit scoring, referrals, and multiple ways to apply in one suite. Choose LifeShack when your target is already clear and you want a focused application pipeline.",
    competitorSummary:
      "Jobright combines personalized matching, reviewable application autofill, an automated-apply Agent, tailored resumes, insider-connection recommendations, and an AI career copilot.",
    lifeShackSummary:
      "LifeShack focuses on turning search preferences into a recurring pipeline of relevant, tailored, submitted, and tracked applications.",
    tableRows: [
      {
        label: "Primary workflow",
        competitor: "Discover and prioritize jobs, then use autofill and tailoring tools.",
        lifeShack: "Set preferences and delegate more of the application workflow.",
      },
      {
        label: "Discovery",
        competitor: "Large personalized feed, match explanations, and early alerts.",
        lifeShack: "Search preferences guide the roles pursued by the application agent.",
      },
      {
        label: "Application execution",
        competitor: "Reviewable one-click autofill plus an advertised automated-apply Agent.",
        lifeShack: "Ongoing application preparation, submission, and tracking.",
      },
      {
        label: "Networking",
        competitor: "Insider and alumni connection recommendations.",
        lifeShack: "Keeps time available for networking by reducing application work.",
      },
      {
        label: "Best fit",
        competitor: "Job discovery and prioritization.",
        lifeShack: "Application volume and consistency.",
      },
    ],
    sections: [
      {
        heading: "How Jobright Works",
        body: [
          "Jobright starts with job discovery. It uses a candidate profile to recommend roles, explain fit, surface potential insider connections, tailor resumes, and help complete applications.",
          "Its current official pages describe two application paths: one-click autofill that the candidate submits and an AI Agent that matches roles, customizes a resume, applies, and tracks submissions.",
          "The homepage FAQ describes primarily U.S. coverage and daily limits on some free features. Its broad free-access language does not establish unlimited free Agent applications. Confirm the candidate plan's actual price, allowance, supported forms, and review controls.",
        ],
      },
      {
        heading: "Where LifeShack Differs",
        body: [
          "LifeShack is centered on application execution. Once the candidate defines the target, the product is designed to keep relevant submissions moving instead of requiring a fresh decision for every form.",
          "The distinction is focus rather than a simple manual-versus-automated split: Jobright is a broad discovery and career suite, while LifeShack is a narrower delegated application pipeline.",
        ],
      },
      {
        heading: "Bottom Line",
        body: [
          "Use Jobright when matching, job discovery, referral context, and multiple application modes matter most.",
          "Use LifeShack when you already know what you want and need a dependable application pipeline.",
        ],
      },
    ],
    chooseCompetitor: [
      "You want match explanations and a large job feed.",
      "You value suggested insider connections.",
      "You want both reviewable autofill and an advertised automated-apply Agent.",
    ],
    chooseLifeShack: [
      "You need consistent applications after setting preferences.",
      "You want submission work handled, not only accelerated.",
      "You want application tracking tied to an automated workflow.",
    ],
    faqs: [
      {
        question: "Is Jobright an auto-apply tool?",
        answer:
          "Yes. Jobright advertises an AI Agent that can match roles, customize a resume, apply, and track submissions. It also offers a separate one-click autofill workflow in which the candidate submits the application.",
      },
      {
        question: "Which tool is better for job discovery?",
        answer:
          "Jobright is the stronger fit for discovery and prioritization. LifeShack is the stronger fit for recurring application execution.",
      },
    ],
    sources: [
      { label: "Jobright official product page", href: "https://jobright.ai/" },
      { label: "Jobright AI Agent", href: "https://jobright.ai/ai-agent" },
      { label: "Jobright one-click autofill", href: "https://jobright.ai/job-autofill" },
      { label: "Jobright cover-letter tool", href: "https://jobright.ai/tools/cover-letter-generator" },
      { label: "Jobright application tracker", href: "https://jobright.ai/tools/job-tracker" },
    ],
  },
  {
    slug: "jobcopilot",
    relatedComparisonSlugs: ["aiapply"],
    name: "JobCopilot",
    category: "Cloud application agent with matching and career tools",
    title: "LifeShack vs JobCopilot",
    metaTitle: "LifeShack vs JobCopilot | AI Auto Apply Comparison",
    metaDescription:
      "Compare LifeShack and JobCopilot for automated applications, job matching, tailored resumes, review controls, tracking, and pricing fit.",
    officialUrl: "https://jobcopilot.com/",
    pricingNote:
      "Premium starts from $0.93/day for up to 20 matches; Elite from $1.05/day for up to 50. Confirm the actual weekly, monthly or quarterly charge; a daily-equivalent headline is not a one-day purchase.",
    bestFor: [
      "Candidates who want a configurable cloud application copilot",
      "Job seekers who want matching and applications in one product",
      "Applicants who want multiple career-preparation tools",
    ],
    watchouts: [
      "Daily volume varies by plan",
      "Higher automation still depends on precise preferences",
      "The broad career-tool suite may be unnecessary for a focused search",
    ],
    verdict:
      "Both products automate applications. LifeShack is the clearer choice for a focused application pipeline with simple controls; JobCopilot suits candidates who want a broader copilot suite.",
    competitorSummary:
      "JobCopilot offers cloud-based job matching and application automation alongside resume, cover-letter, tracker, interview, and career tools.",
    lifeShackSummary:
      "LifeShack concentrates on the core loop of finding relevant roles, tailoring materials, submitting applications, and showing candidates what moved.",
    tableRows: [
      {
        label: "Primary workflow",
        competitor: "Configure one or more copilots with daily match and application limits.",
        lifeShack: "Configure a focused search and run a recurring application pipeline.",
      },
      {
        label: "Application volume",
        competitor: "Daily match volume varies across published plans.",
        lifeShack: "Monthly application credits vary by LifeShack plan.",
      },
      {
        label: "Career tools",
        competitor: "Resume, cover letter, interview, and negotiation tools.",
        lifeShack: "Tailored materials connected directly to applications.",
      },
      {
        label: "Control",
        competitor: "Copilot training and save-for-review options.",
        lifeShack: "Preferences, pause controls, tracking, and company blocklists.",
      },
      {
        label: "Best fit",
        competitor: "A broad job-search copilot suite.",
        lifeShack: "A focused auto-apply execution layer.",
      },
    ],
    sections: [
      {
        heading: "How JobCopilot Works",
        body: [
          "JobCopilot lets candidates configure copilots that match and apply to jobs in the background. Published plans vary by daily match volume and access to tailored resumes.",
          "The product also includes a broad collection of resume, cover-letter, interview, and career-advice tools.",
        ],
      },
      {
        heading: "Where LifeShack Differs",
        body: [
          "LifeShack keeps its core promise narrower: turn preferences and a candidate profile into relevant submitted applications with visibility and controls.",
          "Candidates should compare targeting controls, supported workflows, included application volume, and how much review they want before choosing.",
        ],
      },
      {
        heading: "Bottom Line",
        body: [
          "Use JobCopilot if you want a broad suite organized around configurable copilots.",
          "Use LifeShack if you want a focused application agent and a simple path from setup to submitted jobs.",
        ],
      },
    ],
    chooseCompetitor: [
      "You want multiple copilots or a broad career toolkit.",
      "You value explicit daily match limits.",
      "You want save-for-review controls.",
    ],
    chooseLifeShack: [
      "You want a focused, recurring application workflow.",
      "You value company blocklists and pause controls.",
      "You want tailored materials tied directly to submissions.",
    ],
    faqs: [
      {
        question: "Do LifeShack and JobCopilot both apply automatically?",
        answer:
          "Yes. Both products are designed to reduce manual application work, although their plans, controls, supported workflows, and broader tool sets differ.",
      },
      {
        question: "Which one has more career tools?",
        answer:
          "JobCopilot publicly lists a broader set of standalone interview, negotiation, and career tools. LifeShack is more tightly focused on application execution.",
      },
    ],
    sources: [
      { label: "JobCopilot official site", href: "https://jobcopilot.com/" },
      { label: "JobCopilot official pricing", href: "https://jobcopilot.com/pricing/" },
    ],
  },
  {
    slug: "jobscan",
    name: "Jobscan",
    category: "ATS resume optimization and review-first application tools",
    title: "LifeShack vs Jobscan",
    metaTitle: "LifeShack vs Jobscan | ATS Resume and Auto Apply Comparison",
    metaDescription:
      "Compare LifeShack and Jobscan for ATS resume optimization, keyword matching, resume building, review-first auto apply, and application automation.",
    officialUrl: "https://www.jobscan.co/",
    pricingNote:
      "Premium costs $49.95/month or $89.95 every three months. Auto Apply advertises extra credit packs from $8.50 for five and a monthly top-up to two credits for Premium. Check account allowances and discounts; $8.50 is not the Premium subscription price.",
    bestFor: [
      "Candidates focused on ATS compatibility and keyword alignment",
      "Applicants who want to review every opportunity before it is sent",
      "Job seekers who want resume analysis and editing tools",
    ],
    watchouts: [
      "Resume optimization remains the center of the workflow",
      "Candidates still spend time reviewing and approving opportunities",
      "Premium limits and features can change",
    ],
    verdict:
      "Choose Jobscan when resume quality and ATS alignment are the primary problem. Choose LifeShack when the larger problem is executing a consistent application pipeline.",
    competitorSummary:
      "Jobscan is known for matching resumes to job descriptions, identifying missing keywords, checking ATS formatting, and providing resume-building and review-first application tools.",
    lifeShackSummary:
      "LifeShack uses candidate preferences and materials to pursue relevant jobs, tailor application documents when needed, submit applications, and track activity.",
    tableRows: [
      {
        label: "Primary workflow",
        competitor: "Optimize a resume, find matched jobs, draft applications, then review and approve sending.",
        lifeShack: "Find, tailor, submit, and track matching applications.",
      },
      {
        label: "Resume depth",
        competitor: "Detailed ATS, keyword, format, and job-description analysis.",
        lifeShack: "Optional tailoring connected to each application.",
      },
      {
        label: "Application control",
        competitor: "Every application field is editable before the candidate approves submission.",
        lifeShack: "A more delegated workflow with preferences, pause, and blocklists.",
      },
      {
        label: "Output",
        competitor: "Resume feedback plus prepared/submitted applications and status tracking in Auto Apply.",
        lifeShack: "A tracked pipeline of submitted applications.",
      },
      {
        label: "Best fit",
        competitor: "Improving application materials.",
        lifeShack: "Increasing relevant application execution.",
      },
    ],
    sections: [
      {
        heading: "How Jobscan Works",
        body: [
          "Jobscan compares a resume with a job description and reports missing skills, keywords, format issues, and other ATS-related considerations.",
          "Its Auto Apply workflow also finds matches, drafts answers, allows editing of every field and submits only after approval. This is a different control model from an unattended agent, not just a resume report.",
        ],
      },
      {
        heading: "Where LifeShack Differs",
        body: [
          "LifeShack begins after a candidate has defined the search. It is designed to keep finding and submitting matching applications instead of optimizing one document at a time.",
          "Both products support application execution. Jobscan emphasizes review before every submission; LifeShack delegates sending after activation, with preferences, pause and company-blocking controls. Choose based on the control model you need, not an assumption that Jobscan cannot submit applications.",
        ],
      },
      {
        heading: "Bottom Line",
        body: [
          "Use Jobscan when ATS alignment and resume feedback are the immediate need.",
          "Use LifeShack when enough relevant applications are not getting completed.",
        ],
      },
    ],
    chooseCompetitor: [
      "You want a detailed ATS match report.",
      "You prefer to review every application.",
      "You are willing to optimize documents job by job.",
    ],
    chooseLifeShack: [
      "You want more of the application loop delegated.",
      "You need consistent application volume.",
      "You want application controls and tracking in one workflow.",
    ],
    faqs: [
      {
        question: "Is LifeShack an ATS resume scanner?",
        answer:
          "No. LifeShack can tailor application materials, but its core product is the application workflow rather than a standalone resume score.",
      },
      {
        question: "Does Jobscan offer auto apply?",
        answer:
          "Jobscan's official site advertises a review-first auto-apply workflow in which users review opportunities before they are sent.",
      },
    ],
    sources: [
      { label: "Jobscan official product page", href: "https://www.jobscan.co/" },
      { label: "Jobscan resume scanner", href: "https://www.jobscan.co/resume-scanner" },
      { label: "Jobscan Auto Apply", href: "https://www.jobscan.co/auto-apply" },
      { label: "Jobscan public subscription plans", href: "https://app.jobscan.co/plan" },
    ],
  },
];

const allCompetitorPages: CompetitorPage[] = [
  ...researchedCompetitors,
  ...supplementalCompetitors,
  ...expansionCompetitors,
];

export const competitorProfiles: CompetitorProfile[] = allCompetitorPages.map(
  (competitor) => {
    const metadata = profileMetadata[competitor.slug];

    if (!metadata) {
      throw new Error(`Missing SEO profile metadata for ${competitor.slug}`);
    }

    return {
      ...competitor,
      ...metadata,
      pricingSummary: metadata.pricingSummary,
      verifiedAt: metadata.verifiedAt ?? COMPETITOR_REVIEWED_AT,
      verifiedOn: metadata.verifiedOn ?? COMPETITOR_VERIFIED_ON,
    };
  }
);

export const lifeShackProfile: ProductSummary = {
  slug: "lifeshack",
  name: "LifeShack",
  category: "AI job-search and application agent",
  officialUrl: "https://www.lifeshack.com/",
  productModel: "Cloud application agent with preferences, tailoring, and tracking",
  workflow: "cloud_agent",
  capabilities: {
    jobDiscovery: "yes",
    autofill: "yes",
    autoSubmit: "yes",
    resumeTailoring: "yes",
    coverLetters: "yes",
    tracking: "yes",
  },
  pricingSummary:
    `${FREE_APPLICATION_CREDITS_DESCRIPTION} Paid plans provide higher application volume.`,
  freeAccess: FREE_APPLICATION_CREDITS_DESCRIPTION,
  userControl: "Preferences, pause controls, application tracking, and company blocklists.",
  bestFit: "Candidates who want relevant applications moving without completing every form themselves.",
  limitation: "Applications are sent without per-application review or approval. Only supported applications can be completed; keep preferences and candidate information accurate.",
  verifiedAt: COMPETITOR_REVIEWED_AT,
  verifiedOn: COMPETITOR_VERIFIED_ON,
};

const alternativePageInputs: Array<
  Omit<AlternativePage, "title" | "metaDescription" | "competitorName"> & {
    title?: string;
    metaDescription?: string;
  }
> = [
  {
    slug: "simplify",
    metaDescription:
      "Compare Simplify.jobs alternatives for autofill, job tracking, resume tools, and hands-off applications. See when LifeShack, Huntr, Teal, or Jobright fits.",
    directAnswer:
      "Choose Simplify Copilot for free browser autofill and tracking while you review and submit applications yourself. Autopilot is in private early access for a subset of paid members: select jobs, then send without a final field-by-field review. Choose LifeShack when your target is clear and you want supported applications prepared, submitted, and tracked after setup.",
    reasonsToSwitch: [
      "You want delegated submission but do not have access to Simplify's private-early-access Autopilot.",
      "You want to move beyond Copilot's browser workflow without opening every form yourself.",
      "You need application volume tied to preferences, tailoring, and tracking.",
    ],
    recommendationSlugs: [
      "lifeshack",
      "huntr",
      "teal",
      "jobright",
      "jobscan",
      "careerflow",
      "lazyapply",
    ],
    verdictHeading:
      "Compare Copilot, private-early-access Autopilot, and LifeShack by the workflow you can access.",
    verdictBody:
      "Simplify Copilot offers free, reviewable autofill and tracking. Private-early-access Autopilot beta submits user-selected jobs without a final field-by-field review; confirm paid-member access, supported forms and the weekly allowance. LifeShack offers supported application handling after setup. LifeShack created this page; use the official sources and free application credits to verify the fit.",
    ctaLabel: `Start with ${FREE_TRIAL_APPLICATION_CREDITS} free application credits`,
    ctaHeading:
      "See whether delegated applications beat another autofill workflow.",
    ctaDescription:
      "Set your target once, start with free application credits, and compare LifeShack with reviewing and submitting forms through Simplify Copilot. No credit card required.",
  },
  {
    slug: "lazyapply",
    metaDescription:
      "Compare LazyApply alternatives by pricing, targeting control, resume tailoring, and application workflow. See how LifeShack, LoopCV, and JobCopilot differ.",
    directAnswer:
      "Choose LifeShack for guided application execution, LoopCV for configurable job-search loops, JobCopilot for a broad cloud copilot, or Simplify Copilot when you want to review and submit each application yourself.",
    reasonsToSwitch: [
      "You want relevance controls to matter as much as raw application count.",
      "You want tailored materials and visibility connected to each submission.",
      "You prefer testing a workflow before committing to a long billing term.",
    ],
    recommendationSlugs: [
      "lifeshack",
      "jobcopilot",
      "loopcv",
      "aiapply",
      "applypass",
      "simplify",
      "jobright",
    ],
  },
  {
    slug: "loopcv",
    directAnswer:
      "Compare LoopCV alternatives by submission controls and allowances. LifeShack offers preference-led applications, JobCopilot offers cloud automation, ApplyPass pairs weekly automated volume with optional coaching, and Simplify Copilot keeps final submission manual.",
    reasonsToSwitch: [
      "You want a simpler application-first workflow instead of configuring recurring loops and outreach.",
      "You want tailored application materials connected directly to submissions.",
      "You want clearer controls around preferences, pausing, and blocked companies.",
    ],
    recommendationSlugs: [
      "lifeshack",
      "jobcopilot",
      "aiapply",
      "applypass",
      "lazyapply",
      "jobright",
      "simplify",
    ],
  },
  {
    slug: "jobright",
    directAnswer:
      "Choose Jobright when you want discovery, fit scoring, referrals, and a broad AI job-search suite with both autofill and an automated-apply Agent. Choose LifeShack when your target is already clear and you want a focused application pipeline with preferences, submission, and tracking in one workflow.",
    reasonsToSwitch: [
      "You already know your target roles and need more applications completed.",
      "You need an execution layer more than another personalized job feed.",
      "You want to spend less time reviewing and driving individual forms.",
    ],
    recommendationSlugs: [
      "lifeshack",
      "simplify",
      "teal",
      "careerflow",
      "huntr",
      "jobcopilot",
      "jobscan",
    ],
    verdictHeading:
      "Choose Jobright for discovery breadth; choose LifeShack for a focused application pipeline.",
    verdictBody:
      "Jobright is stronger when matching, referrals, resume tools, and multiple ways to apply belong in one broad suite. LifeShack is the clearer fit when the target is already defined and dependable application execution is the main goal. LifeShack created this page; use the official sources and free application credits to verify the fit.",
    ctaLabel: `Start with ${FREE_TRIAL_APPLICATION_CREDITS} free application credits`,
    ctaHeading:
      "See whether execution—not more discovery—is your missing layer.",
    ctaDescription:
      "Set your target, start with free application credits, and judge LifeShack by the supported applications it can prepare, submit, and track. No credit card required.",
  },
  {
    slug: "jobcopilot",
    directAnswer:
      "LifeShack is the strongest JobCopilot alternative for candidates who want a focused application pipeline. LoopCV offers broader loops and outreach, AIApply offers a wider preparation toolkit, and Simplify Copilot is better when every submission should remain manual.",
    reasonsToSwitch: [
      "You want a narrower product centered on submitted applications.",
      "You want preferences, company blocklists, and pause controls in one workflow.",
      "You do not need a large bundle of unrelated career-advice tools.",
    ],
    recommendationSlugs: [
      "lifeshack",
      "loopcv",
      "aiapply",
      "applypass",
      "lazyapply",
      "jobright",
      "simplify",
    ],
  },
  {
    slug: "aiapply",
    directAnswer:
      "The best AIApply alternatives are more specialized. LifeShack focuses on application execution, Jobscan focuses on ATS resume quality, and Huntr or Teal provide a hands-on job-search workspace.",
    reasonsToSwitch: [
      "You want a focused auto-apply product instead of an all-in-one career toolkit.",
      "You prefer a simpler pricing and application-volume decision.",
      "You want the core success measure to be relevant submitted applications.",
    ],
    recommendationSlugs: [
      "lifeshack",
      "jobcopilot",
      "loopcv",
      "jobscan",
      "careerflow",
      "huntr",
      "teal",
    ],
  },
  {
    slug: "teal",
    title: "7 Teal Alternatives for Resumes and Job Tracking",
    metaDescription:
      "Need a Teal alternative? Compare seven tools for resumes, job tracking, and auto apply. See free access, pricing, and who reviews each application.",
    directAnswer:
      "Choose LifeShack when you want supported applications submitted for you, Huntr or Careerflow for another hands-on job-search workspace, or Jobscan when ATS resume analysis is the priority.",
    reasonsToSwitch: [
      "You have organized the search but still are not completing enough applications.",
      "You want application execution instead of another planning workspace.",
      "You need an agent that works after preferences and documents are ready.",
    ],
    recommendationSlugs: [
      "lifeshack",
      "huntr",
      "careerflow",
      "jobscan",
      "simplify",
      "jobright",
      "aiapply",
    ],
  },
  {
    slug: "jobscan",
    metaDescription:
      "Compare Jobscan alternatives for ATS resume analysis, keyword matching, job tracking, autofill, and hands-off applications. See when LifeShack fits.",
    directAnswer:
      "The best Jobscan alternatives depend on whether you need resume analysis or application execution. LifeShack handles more of the application loop, while Teal, Huntr, and Careerflow provide alternative resume and tracking workspaces.",
    reasonsToSwitch: [
      "Your resume is ready and the problem is now application consistency.",
      "You want a pipeline of submitted jobs instead of another match score.",
      "You want tailoring to happen inside a broader application workflow.",
    ],
    recommendationSlugs: [
      "lifeshack",
      "teal",
      "huntr",
      "careerflow",
      "simplify",
      "aiapply",
      "jobright",
    ],
  },
  {
    slug: "huntr",
    metaDescription:
      "Compare Huntr alternatives for job tracking, resume tools, autofill, ATS analysis, and hands-off applications. See when LifeShack, Teal, or Jobscan fits.",
    directAnswer:
      "The strongest Huntr alternatives include Teal and Careerflow for self-managed job-search workspaces, Jobscan for ATS analysis, and LifeShack when the goal is to delegate applications rather than track them manually.",
    reasonsToSwitch: [
      "You no longer want to maintain every job card and application yourself.",
      "You want more automation than a tracker and autofill extension provide.",
      "You want application activity to continue after the initial setup.",
    ],
    recommendationSlugs: [
      "lifeshack",
      "teal",
      "careerflow",
      "jobscan",
      "simplify",
      "jobright",
      "aiapply",
    ],
  },
  {
    slug: "careerflow",
    directAnswer:
      "The best Careerflow alternatives are Teal and Huntr for similar career workspaces, Jobscan for ATS resume analysis, and LifeShack for candidates who want the application work executed rather than organized.",
    reasonsToSwitch: [
      "You need applications submitted more than LinkedIn or resume optimization.",
      "You want to reduce hands-on search management.",
      "You want one workflow centered on application output.",
    ],
    recommendationSlugs: [
      "lifeshack",
      "teal",
      "huntr",
      "jobscan",
      "simplify",
      "jobright",
      "aiapply",
    ],
  },
  {
    slug: "applypass",
    directAnswer:
      "ApplyPass automates applications with optional coaching. Compare LifeShack for monthly application allowances, JobCopilot or LoopCV for other cloud workflows, and Simplify Copilot when you want to review and submit forms yourself.",
    reasonsToSwitch: [
      "You prefer monthly application allowances to weekly quotas.",
      "You want to control preferences and application activity directly.",
      "You want a lower-commitment way to test application automation.",
    ],
    recommendationSlugs: [
      "lifeshack",
      "jobcopilot",
      "loopcv",
      "aiapply",
      "lazyapply",
      "jobright",
      "simplify",
    ],
  },
  {
    slug: "sonara",
    title: "7 Sonara AI Alternatives for Automated Job Applications",
    metaDescription:
      "Compare seven Sonara AI alternatives by job matching, auto apply, pricing, and review controls. Find the right level of automation for your job search.",
    directAnswer:
      "The best Sonara alternatives provide clearer choices across application agents, copilots, and managed services. LifeShack is the focused application agent; JobCopilot and LoopCV provide broad cloud automation; Simplify Copilot is the manual-control option.",
    reasonsToSwitch: [
      "You want clearer public information about features, controls, and plan fit.",
      "You want tailored materials and application tracking in the same workflow.",
      "You want a product aligned with your preferred level of review and automation.",
    ],
    recommendationSlugs: [
      "lifeshack",
      "jobcopilot",
      "loopcv",
      "aiapply",
      "applypass",
      "jobright",
      "simplify",
    ],
  },
  {
    slug: "jobhire-ai",
    metaDescription:
      "Compare JobHire.AI alternatives for auto apply, resume tailoring, review controls, free access, pricing clarity, role coverage, and application tracking.",
    directAnswer:
      "Choose LifeShack for a focused agent with free starting credits, ApplyGhost for technical-role automation, Massive for its matched job inventory, or ApplyGenie when every submission needs manual review.",
    reasonsToSwitch: [
      "You want to test application automation before paying.",
      "You want exact public starting terms instead of registering to see prices.",
      "You prefer a product specialized around application execution.",
    ],
    recommendationSlugs: [
      "lifeshack",
      "applyghost",
      "massive",
      "jobcopilot",
      "aiapply",
      "scale-jobs",
      "applygenie",
    ],
  },
  {
    slug: "applyghost",
    metaDescription:
      "Compare ApplyGhost alternatives for technical and nontechnical roles, server-side auto apply, match controls, resume tailoring, application limits, and pricing.",
    directAnswer:
      "Choose LifeShack for broader role coverage, Massive for a matched-job autopilot, JobHire.AI for a larger job-search suite, or ApplyGenie for review-before-submit autofill.",
    reasonsToSwitch: [
      "Your target roles extend beyond technical and tech-adjacent categories.",
      "You prefer monthly application credits to weekly quotas.",
      "You want a different balance between automatic submission and manual review.",
    ],
    recommendationSlugs: [
      "lifeshack",
      "massive",
      "jobhire-ai",
      "jobcopilot",
      "loopcv",
      "applygenie",
      "scale-jobs",
    ],
  },
  {
    slug: "scale-jobs",
    metaDescription:
      "Compare Scale.jobs alternatives for human-managed applications, AI auto apply, upfront cost, free access, application volume, document support, and tracking.",
    directAnswer:
      "Choose LifeShack for a software agent with free starting credits, ApplyPass for weekly automated applications with optional coaching, or ApplyGenie when you want to review and submit supported forms yourself.",
    reasonsToSwitch: [
      "You prefer software automation to human-managed applications.",
      "You want to start without paying for a large application package.",
      "You prefer a recurring software workflow to coordinating a human-managed queue.",
    ],
    recommendationSlugs: [
      "lifeshack",
      "applypass",
      "jobhire-ai",
      "massive",
      "applyghost",
      "jobcopilot",
      "applygenie",
    ],
  },
  {
    slug: "massive",
    metaDescription:
      "Compare Massive alternatives for autopilot applications, matching, custom resumes, visa filters, submission controls, volume, and pricing clarity.",
    directAnswer:
      "Choose LifeShack for transparent free starting access, ApplyGhost for tech-only match controls, JobHire.AI for a broad suite, or Scale.jobs when a human should handle the applications.",
    reasonsToSwitch: [
      "You want public starting terms before creating an account.",
      "You need controls or role coverage that differ from Massive's matched inventory.",
      "You want to compare a software agent with manual-review and human-managed options.",
    ],
    recommendationSlugs: [
      "lifeshack",
      "applyghost",
      "jobhire-ai",
      "jobcopilot",
      "loopcv",
      "scale-jobs",
      "applygenie",
    ],
  },
  {
    slug: "applygenie",
    metaDescription:
      "Compare ApplyGenie alternatives for LinkedIn and Lever autofill, hands-off auto apply, supported sites, final review, discovery, packs, and trials.",
    directAnswer:
      "Choose LifeShack when you want applications submitted for you, Simplify Copilot for a broader autofill ecosystem, Jobright for discovery, or Teal and Huntr for hands-on search organization.",
    reasonsToSwitch: [
      "You want the workflow to continue without opening every application form.",
      "You apply beyond LinkedIn Easy Apply and Lever.",
      "You need job discovery, submission, and tracking in one recurring workflow.",
    ],
    recommendationSlugs: [
      "lifeshack",
      "simplify",
      "huntr",
      "jobright",
      "jobscan",
      "teal",
      "careerflow",
    ],
  },
];

export const alternativePages: AlternativePage[] = alternativePageInputs.map(
  (page) => {
    const competitor = competitorProfiles.find(
      (profile) => profile.slug === page.slug
    );

    if (!competitor) {
      throw new Error(`Missing competitor for alternatives page ${page.slug}`);
    }

    return {
      ...page,
      competitorName: competitor.name,
      title: page.title ?? `7 Best ${competitor.name} Alternatives for AI Job Search in 2026`,
      metaDescription:
        page.metaDescription ??
        `Compare seven ${competitor.name} alternatives for job discovery, autofill, auto apply, resume tailoring, tracking, pricing fit, and user control.`,
    };
  }
);

export function getCompetitorProfile(
  slug: string
): CompetitorProfile | undefined {
  return competitorProfiles.find((profile) => profile.slug === slug);
}

export function getAlternativePage(
  slug: string
): AlternativePage | undefined {
  return alternativePages.find((page) => page.slug === slug);
}

export function getProductSummary(slug: string): ProductSummary | undefined {
  if (slug === lifeShackProfile.slug) return lifeShackProfile;
  return getCompetitorProfile(slug);
}

export function getAlternativeRecommendations(
  page: AlternativePage
): ProductSummary[] {
  return page.recommendationSlugs
    .map(getProductSummary)
    .filter((product): product is ProductSummary => Boolean(product));
}

export function assertCompetitorSeoData({
  now = Date.now(),
}: {
  now?: number;
} = {}): void {
  const slugs = competitorProfiles.map((profile) => profile.slug);
  const uniqueSlugs = new Set(slugs);

  if (uniqueSlugs.size !== slugs.length) {
    throw new Error("Duplicate competitor slugs are not allowed");
  }

  alternativePages.forEach((page) => {
    if (page.recommendationSlugs.length !== 7) {
      throw new Error(`${page.slug} must recommend exactly seven alternatives`);
    }
    if (new Set(page.recommendationSlugs).size !== 7) {
      throw new Error(`${page.slug} contains duplicate alternatives`);
    }
    if (page.recommendationSlugs.includes(page.slug)) {
      throw new Error(`${page.slug} cannot recommend itself`);
    }
    page.recommendationSlugs.forEach((slug) => {
      if (!getProductSummary(slug)) {
        throw new Error(`${page.slug} references unknown alternative ${slug}`);
      }
    });
  });

  competitorProfiles.forEach((profile) => {
    const verifiedOn = new Date(`${profile.verifiedOn}T00:00:00Z`);
    const claimAgeInDays =
      (now - verifiedOn.getTime()) / (1000 * 60 * 60 * 24);

    if (!Number.isFinite(verifiedOn.getTime())) {
      throw new Error(`${profile.slug} has an invalid claim-verification date`);
    }
    if (claimAgeInDays > CLAIM_FRESHNESS_DAYS) {
      throw new Error(
        `${profile.slug} pricing and feature claims are ${Math.floor(
          claimAgeInDays
        )} days old. Reverify its official sources and update the profile date before building.`
      );
    }
  });
}
